import { useEffect, useState } from "react";
import { FileText, Link2, Loader2, Plus, Save, Trash2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { api, download, errMsg, money } from "@/lib/api";

const STATUT = { regle: ["Réglé", "bg-emerald-50 text-emerald-700"], partiel: ["Partiellement réglé", "bg-sky-50 text-sky-700"], attente: ["En attente", "bg-amber-50 text-amber-800"] };
const fr = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2).replace(".", ","));
const nb = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const versOptions = (y) => [y + 1, y + 2].flatMap((z) => [
  ...[1, 2, 3, 4].map((q) => [`${z}-T${q}`, `T${q} ${z}`]), [`${z}-A`, `Acompte ${z}`], [String(z), `Solde de la déclaration annuelle ${z}`],
]);

// Montant payé en trop : calcul automatique (payé − déclaré), corrigeable
const Montant = ({ t, onChange }) => {
  const [v, setV] = useState(fr(t.montant));
  useEffect(() => { setV(fr(t.montant)); }, [t.montant]);
  const enregistrer = async (montant) => {
    if (montant !== null && (Number.isNaN(montant) || montant < 0)) { toast.error("Montant invalide"); return; }
    try { await api.put(`/trop-payes/${t.annee}/montant`, { montant }); toast.success("Montant enregistré"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-slate-600">Payé en trop</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} inputMode="decimal" className="h-8 w-32 tabular" data-testid={`trop-paye-${t.annee}-montant`} />
      <Button size="sm" variant="outline" onClick={() => enregistrer(nb(v))} data-testid={`trop-paye-${t.annee}-montant-save`}><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
      {t.modifie && <button type="button" className="text-xs text-emerald-700 hover:underline" onClick={() => enregistrer(null)} data-testid={`trop-paye-${t.annee}-montant-auto`}>Revenir au calcul ({money(t.calcule)})</button>}
      {!t.modifie && <span className="text-xs text-slate-500">calculé : payé − déclaré</span>}
    </div>
  );
};

const Reglements = ({ t, onChange }) => {
  const retirer = async (r) => {
    try {
      await (r.type === "releve" ? api.delete(`/trop-payes/lier/${r.id}`) : api.delete(`/trop-payes/${t.annee}/reglements/${r.id}`));
      toast.success("Règlement retiré"); onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!t.reglements.length) return <p className="mt-2 text-xs text-slate-500" data-testid={`trop-paye-${t.annee}-aucun-reglement`}>Aucun remboursement ni report pour l'instant.</p>;
  return (
    <div className="mt-2 space-y-1" data-testid={`trop-paye-${t.annee}-reglements`}>
      {t.reglements.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs" data-testid={`trop-paye-reglement-${r.id}`}>
          <span className="font-mono text-slate-500">{r.date}</span>
          <span className="order-last w-full sm:order-none sm:w-auto sm:flex-1 min-w-0 break-words">{r.libelle}{r.description ? ` · ${r.description}` : ""}{r.note ? ` · ${r.note}` : ""}</span>
          <span className="font-mono">{money(r.montant)}</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => retirer(r)} title={r.type === "releve" ? "Délier ce dépôt" : "Retirer"} data-testid={`trop-paye-reglement-${r.id}-retirer`}>
            {r.type === "releve" ? <Unlink className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ))}
    </div>
  );
};

// Report sur une période suivante, ou remboursement reçu ailleurs que dans les relevés
const Ajout = ({ t, onChange }) => {
  const [f, setF] = useState({ type: "report", montant: fr(t.reste), date: aujourdhui(), vers: `${t.annee + 1}-A`, note: "" });
  const maj = (k, v) => setF({ ...f, [k]: v });
  const envoyer = async () => {
    const montant = nb(f.montant);
    if (!montant || montant <= 0) { toast.error("Montant invalide"); return; }
    try { await api.post(`/trop-payes/${t.annee}/reglements`, { ...f, montant }); toast.success(f.type === "report" ? "Report enregistré" : "Remboursement enregistré"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-slate-50 p-2" data-testid={`trop-paye-${t.annee}-ajout`}>
      <Select value={f.type} onValueChange={(v) => maj("type", v)}>
        <SelectTrigger className="h-8 w-48 text-xs" data-testid={`trop-paye-${t.annee}-type`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="report">Reporté (appliqué)</SelectItem><SelectItem value="remboursement">Remboursé hors relevé</SelectItem></SelectContent>
      </Select>
      {f.type === "report" && (
        <Select value={f.vers} onValueChange={(v) => maj("vers", v)}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid={`trop-paye-${t.annee}-vers`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">{versOptions(t.annee).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Input value={f.montant} onChange={(e) => maj("montant", e.target.value)} inputMode="decimal" className="h-8 w-28 tabular" data-testid={`trop-paye-${t.annee}-ajout-montant`} />
      <Input type="date" value={f.date} onChange={(e) => maj("date", e.target.value)} className="h-8 w-36" data-testid={`trop-paye-${t.annee}-ajout-date`} />
      <Input value={f.note} onChange={(e) => maj("note", e.target.value)} placeholder="Note (avis de cotisation…)" className="h-8 w-48" data-testid={`trop-paye-${t.annee}-ajout-note`} />
      <Button size="sm" onClick={envoyer} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`trop-paye-${t.annee}-ajout-ok`}><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
    </div>
  );
};

// Dépôts de Revenu Québec trouvés dans les relevés : à confirmer comme remboursement
const Candidats = ({ t, onChange }) => {
  const lier = async (c) => {
    try { await api.post(`/trop-payes/${t.annee}/lier/${c.id}`); toast.success("Remboursement lié"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!t.candidats.length) return null;
  return (
    <div className="mt-3" data-testid={`trop-paye-${t.annee}-candidats`}>
      <p className="text-xs font-medium text-slate-700">Remboursements possibles trouvés dans les relevés</p>
      {t.candidats.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-slate-100 py-1 text-xs" data-testid={`trop-paye-candidat-${c.id}`}>
          <span className="font-mono text-slate-500">{c.date}</span><span className="font-mono">{money(c.montant)}</span>
          <span className="w-full sm:w-auto sm:flex-1 min-w-0 break-words">{c.description}</span>
          <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => lier(c)} data-testid={`trop-paye-candidat-${c.id}-lier`}><Link2 className="h-3.5 w-3.5 mr-1" />C'est le remboursement</Button>
        </div>
      ))}
    </div>
  );
};

const Carte = ({ t, onChange }) => {
  const [lib, cls] = STATUT[t.statut];
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={`trop-paye-${t.annee}`}>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <span className="font-semibold">Trop-payé {t.annee}</span>
        <span className={`rounded px-2 py-0.5 text-xs ${cls}`} data-testid={`trop-paye-${t.annee}-statut`}>{lib}</span>
        <span className="ml-auto text-sm">Réglé {money(t.regle)} · <b data-testid={`trop-paye-${t.annee}-reste`}>Reste {money(t.reste)}</b>{t.surplus > 0 && <span className="ml-1 text-xs text-sky-700">(reçu {money(t.surplus)} de plus)</span>}</span>
      </div>
      {t.note && <p className="mb-2 text-xs text-slate-600" data-testid={`trop-paye-${t.annee}-note`}>{t.note}</p>}
      {t.reste > 0.01 && (
        <Button size="sm" variant="outline" className="mb-2" data-testid={`trop-paye-${t.annee}-lettre`}
          onClick={() => download(`/trop-payes/${t.annee}/lettre`, {}, `Demande de remboursement TPS-TVQ ${t.annee}.pdf`).catch((e) => toast.error(errMsg(e)))}>
          <FileText className="h-3.5 w-3.5 mr-1" />Lettre de remboursement (PDF)
        </Button>
      )}
      {t.reste > 0.01 && <SuiviLettre key={t.lettre_envoyee || ""} t={t} onChange={onChange} />}
      <Montant t={t} onChange={onChange} />
      <Reglements t={t} onChange={onChange} />
      {t.reste > 0.01 && <Candidats t={t} onChange={onChange} />}
      {t.reste > 0.01 && <Ajout key={t.reste} t={t} onChange={onChange} />}
    </div>
  );
};

// Date d'envoi de la lettre ; rappel par courriel 60 jours après si aucun remboursement
const SuiviLettre = ({ t, onChange }) => {
  const [v, setV] = useState(t.lettre_envoyee || "");
  const enregistrer = async (date) => {
    try { await api.put(`/trop-payes/${t.annee}/lettre-envoyee`, { date: date || null }); toast.success(date ? "Date d'envoi enregistrée" : "Date d'envoi effacée"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`trop-paye-${t.annee}-suivi-lettre`}>
      <span className="text-slate-600">Lettre envoyée le</span>
      <Input type="date" max={new Date().toISOString().slice(0, 10)} value={v} onChange={(e) => setV(e.target.value)} className="h-8 w-40" data-testid={`trop-paye-${t.annee}-lettre-date`} />
      <Button size="sm" variant="outline" onClick={() => enregistrer(v)} disabled={!v} data-testid={`trop-paye-${t.annee}-lettre-date-save`}><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
      {t.lettre_envoyee && <button type="button" className="text-emerald-700 hover:underline" onClick={() => { setV(""); enregistrer(null); }} data-testid={`trop-paye-${t.annee}-lettre-date-effacer`}>Effacer</button>}
      {t.relance_le && <span className="text-sky-700" data-testid={`trop-paye-${t.annee}-relance`}>Rappel par courriel le {t.relance_le} si aucun remboursement n'est inscrit</span>}
    </div>
  );
};

// Trop-payé entré à la main (ex. acomptes versés avant les relevés importés)
const Nouveau = ({ onChange }) => {
  const an = new Date().getFullYear();
  const [f, setF] = useState({ annee: String(an - 1), montant: "", note: "" });
  const ajouter = async () => {
    const montant = nb(f.montant);
    if (!(montant > 0)) { toast.error("Montant invalide"); return; }
    try { await api.put(`/trop-payes/${f.annee}/montant`, { montant, note: f.note }); toast.success("Trop-payé ajouté"); setF({ ...f, montant: "", note: "" }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 p-2" data-testid="trop-paye-nouveau">
      <span className="text-xs font-medium text-slate-700">Ajouter un trop-payé</span>
      <Select value={f.annee} onValueChange={(v) => setF({ ...f, annee: v })}>
        <SelectTrigger className="h-8 w-24 text-xs" data-testid="trop-paye-nouveau-annee"><SelectValue /></SelectTrigger>
        <SelectContent>{[1, 2, 3, 4].map((i) => String(an - i)).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} inputMode="decimal" placeholder="montant" className="h-8 w-28 tabular" data-testid="trop-paye-nouveau-montant" />
      <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (ex. acomptes déjà versés, courriel du comptable)" className="h-8 flex-1 min-w-[12rem]" data-testid="trop-paye-nouveau-note" />
      <Button size="sm" onClick={ajouter} className="bg-emerald-700 hover:bg-emerald-800" data-testid="trop-paye-nouveau-ok"><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
    </div>
  );
};

// Montants payés en trop à Revenu Québec, suivis jusqu'au remboursement ou au report
export const TropPayesPanel = ({ version, onChange }) => {
  const [liste, setListe] = useState(null);
  const [v, setV] = useState(0);
  useEffect(() => { api.get("/trop-payes").then((r) => setListe(r.data)).catch((e) => toast.error(errMsg(e))); }, [version, v]);
  const recharger = () => { setV((x) => x + 1); onChange?.(); };
  return (
    <Panel title="Suivi des trop-payés TPS/TVQ" testId="trop-payes-panel">
      {!liste ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <p className="mb-3 text-xs text-slate-500">Montant payé en trop pour une année (payé − déclaré, ou entré à la main), jusqu'à ce qu'il soit remboursé (dépôt de Revenu Québec lié) ou reporté sur une période suivante. Un report compte comme un paiement de la période choisie dans la conciliation.</p>
          <Nouveau onChange={recharger} />
          {!liste.length ? <p className="text-sm text-slate-500" data-testid="trop-payes-aucun">Aucun montant payé en trop pour les années terminées.</p>
            : <div className="space-y-3">{liste.map((t) => <Carte key={t.annee} t={t} onChange={recharger} />)}</div>}
        </>
      )}
    </Panel>
  );
};
